import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useParams, useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import classNames from 'classnames'
import axios from 'axios'
import { updateCart } from '~redux/shop'
import { groupBy, sortBy } from 'lodash'
import { getNextWorkingDay } from '~helpers/content'
import { pageView } from '~helpers/ga'
import { displayPrice } from '~helpers/price'
import { convertToTitleCase } from '~helpers/main'
import Sprite from '~components/Sprite'
import Button from '~components/ui/Button'
import ButtonLink from '~components/ui/ButtonLink'
import RadioButton from '~components/ui/RadioButton'
import Heading from '~components/shop/Heading'
import CartAccordionItem from '../../shop/CartAccordionItem'
import DatePicker from '~components/ui/DatePicker'
import ShippingCosts from '~components/ShippingCosts'
import QuickOrder from '~components/QuickOrder'
import DeliverySequence from '~components/shop/DeliverySequence'
import InfoBubble from '~components/ui/InfoBubble'
// Import other necessary components and hooks

const ShopCart = ({ updateCart, account, stockists, stockistId, cart }) => {
  const [busy, setBusy] = useState(false)
  const [tax, setTax] = useState(null)
  const [customerReference, setCustomerReference] = useState('')
  const [selectedDispatch, setSelectedDispatch] = useState('asap')
  const [customDispatchDate, setCustomDispatchDate] = useState(new Date(getNextWorkingDay(5)))
  const [quickOrder, setQuickOrder] = useState(false)
  const [shippingMessage, setShippingMessage] = useState(null)
  const [shippingAddress, setShippingAddress] = useState([])
  const [shippingCosts, setShippingCosts] = useState(false)
  const [sequenceId, setSequenceId] = useState(stockistId)
  const [deliveryValid, setDeliveryValid] = useState(true)
  const [created, setCreated] = useState(false)
  const [accordions, setAccordions] = useState([])
  const [accordionsInitialised, setAccordionsInitialised] = useState(false)

  const headerHeight = 100
  const deliveryRef = useRef()

  const dispatchOptions = [{
    label: 'As soon as possible (3-5 days)',
    value: 'asap'
  },
  {
    label: 'Preferred dispatch date',
    value: 'custom'
  }]

  const params = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    pageView()
    setShippingAddressById(sequenceId)
    fetchTax().catch((e) => console.error('Failed to fetch tax:', e))
  }, [stockistId])

  useEffect(() => {
    if (!isBasketEmpty() && !accordionsInitialised) {
      setAccordionList()
      setAccordionsInitialised(true)
    }
  }, [cart])

  useEffect(() => {
    fetchShippingMessage()
  }, [cart, tax])

  useEffect(() => {
    if (params.dialog === 'quick') {
      toggleQuickOrder()
    }

    if (params.dialog === 'shipping') {
      toggleShippingCosts()
    }
  }, [params.dialog])

  const fetchTax = () => {
    return new Promise((resolve, reject) => {
      axios.get('/api/cart/tax').then(response => {
        if (response.data.taxTotal) {
          setTax(response.data.taxTotal)
        }

        resolve()
      }).catch(error => reject(error))
    })
  }

  const fetchShippingMessage = () => {
    const cartAmount = cart?.cart_amount || 0
    const cartTotal = cartAmount + tax
    const currencyCode = cart?.currency?.code

    if (!cart || !currencyCode) {
      return setShippingMessage(null)
    }

    axios.get('/options', { params: { option: 'shipping_costs' } })
      .then(response => {
        const shippingOptions = response.data.fields.shipping_options

        const costs = shippingOptions.filter(cost => {
          if (cost.attributes.country_code === account.stockist.country) {
            return true
          }

          return false
        })

        if (costs[0] && cartTotal <= costs[0].attributes.min_price) {
          const msg = response.data.fields.min_message.replace('{price}', displayPrice(costs[0].attributes.min_price, cart.currency.code))
          return setShippingMessage(msg)
        }
      })
      .catch(error => {
        console.error('Failed to fetch shipping options:', error)
        setShippingMessage(null)
      })
  }

  const updateItem = (itemId, data) => {
    if (busy) return
    setBusy(true)

    axios.put(`/api/cart/items/${itemId}`, data).then(response => {
      updateCart(response.data)
    }).finally(() => {
      setBusy(false)
    })
  }

  const handleDispatchChange = (option) => {
    setSelectedDispatch(option)
  }

  const setShippingAddressById = (id = 0) => {
    const selectedStockist = stockists.find((stockist) => stockist.id === parseInt(id)) || {}
    setShippingAddress([...Object.values(selectedStockist?.address)])
  }

  const toggleQuickOrder = () => {
    setQuickOrder(!quickOrder)
    // Temporary until permanent solution found
    document.body.classList.toggle('overflow-y-hidden')
  }

  const toggleShippingCosts = () => {
    setShippingCosts(!shippingCosts)
    // Temporary until permanent solution found
    document.body.classList.toggle('overflow-y-hidden')
  }

  const handleCustomDispatchDateChange = (day) => {
    setCustomDispatchDate(day)
  }

  const handleCustomerReferenceChange = (e) => {
    const ref = e.target.value.substring(0, 16)
    setCustomerReference(ref)
  }

  const handleDeliverySequenceChange = (value) => {
    if (!value) return
    const id = parseInt(value) || null

    setSequenceId(id)
    setDeliveryValid(true)
    setShippingAddressById(id)
  }

  const scrollToDeliveryDropdown = () => {
    const dropdown = deliveryRef.current
    const { top } = dropdown.getBoundingClientRect()
    const offset = top + window.scrollY - headerHeight

    window.scrollTo({
      top: offset,
      left: 0,
      behavior: 'smooth'
    })
  }

  const submit = () => {
    if (busy) return

    if (!sequenceId) {
      scrollToDeliveryDropdown()
      setDeliveryValid(false)
      return
    }

    setBusy(true)
    setDeliveryValid(true)

    const data = {
      customer_reference: customerReference,
      sequence_id: sequenceId
    }

    if (selectedDispatch === 'custom') {
      data.dispatch_date = customDispatchDate
    }

    axios.post('/api/checkout', data).then(response => {
      axios.delete('/api/cart').finally(() => {
        updateCart(null)
        setCreated(true)
      })
    }).catch(() => setBusy(false))
  }

  const isBasketEmpty = () => {
    return !cart || cart.line_items.physical_items.length < 1
  }

  const isBlocked = () => {
    const status = cart?.status

    //  Stockists with any status value should be blocked.
    //  I.E. 'S', 'R', 'U'
    if (status && status.length >= 1) {
      return true
    }

    return false
  }

  const renderEmptyBasket = () => {
    return (
      <div className="w-full mt-10">
        <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          <div className="flex flex-col items-center px-6 py-10 border border-coral md:p-10 lg:p-15 xl:p-20">
            <div className="my-auto">
              <h2 className="text-sm text-center md:text-md">
                <span className="flex flex-col mx-auto mb-4 border-2 w-15 h-15 rounded-1/2 border-coral" aria-hidden="true">
                  <span className="block m-auto w-11 h-11">
                    <Sprite name="basket" />
                  </span>
                </span>

                Your basket is empty
              </h2>

              <div className="flex flex-col items-center mx-auto mt-10">
                <ButtonLink
                  href="/shop"
                  className="min-w-37"
                  theme="green-to-pink"
                >
                  Start browsing
                </ButtonLink>

                <Button className="mt-4 min-w-37" theme="secondary" onClick={() => toggleQuickOrder()}>
                  Quick order
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const setAccordionList = () => {
    const accordions = []
    const items = groupBy(cart.line_items.physical_items, 'name')

    Object.keys(items).forEach((key) => {
      accordions.push({
        id: key,
        expand: account.stockist.expanded_cart
      })
    })

    setAccordions(accordions)
  }

  const anyAccordionsOpen = () => {
    const list = accordions.filter((item) => (
      item.expand === true
    ))

    return list.length
  }

  const toggle = (id) => {
    setAccordions(prevAccordions => {
      return prevAccordions.map(accordion => {
        if (accordion.id !== id) return accordion
        return { ...accordion, expand: !accordion.expand }
      })
    })
  }

  const toggleExpandAll = (expand) => {
    setAccordions(prevAccordions => {
      return prevAccordions.map(accordion => {
        accordion.expand = expand
        return accordion
      })
    })
  }

  const renderCart = () => {
    const cartItems = groupBy(cart.line_items.physical_items, 'name')

    return (
      <>
        <div className="grid grid-cols-8 col-span-8">
          <span
            className="p-3 font-bold md:p-4"
          >
            Brand
          </span>

          <span className="col-span-2 p-3 font-bold md:p-4">
            Product name
          </span>

          <span className="p-3 font-bold md:p-4">
            Style number
          </span>

          <span className="p-3 font-bold md:p-4">
            Colour
          </span>

          <span className="p-3 font-bold md:p-4">
            Total unit qty
          </span>

          <span className="p-3 font-bold md:p-4">
            Total price
          </span>

          <button
            type="button"
            className="p-3 font-bold text-left bg-transparent md:p-4 hover:underline focus:underline"
            onClick={() => {
              const expand = !anyAccordionsOpen()
              toggleExpandAll(expand)
            }}
          >
            {anyAccordionsOpen() ? 'Collapse all' : 'Expand all'}
          </button>
        </div>

        {accordions.map((accordion) => {
          const group = sortBy(cartItems[accordion.id], item => item.options.find(o => o.name === 'Size').value)

          if (group && group.length > 0) {
            return (
              <CartAccordionItem
                productName={accordion.id}
                currencyCode={cart.currency.code}
                items={group}
                brandName={group[0].brand}
                busy={busy}
                onChange={updateItem.bind(this)}
                key={accordion.id}
                expand={accordion.expand}
                toggle={() => toggle(accordion.id)}
              />
            )
          }

          return null
        })}
      </>
    )
  }

  if (created) {
    return navigate('/shop/confirmation')
  }

  return (
    <>
      {isBlocked() && (
        <div className="w-full mt-10">
          <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
            <div className="p-4 mt-4 font-bold bg-white border rounded-md border-red">
              <Sprite name="error" />
              There is currently an overdue invoice on your account, please contact a member of the finance team to resolve and complete your purchase
            </div>
          </div>
        </div>)}

      <Heading
        title="Basket"
        breadcrumbs={[{
          title: 'Basket'
        }]}
        className="mt-2"
        border={!isBasketEmpty()}
        basket={!isBasketEmpty()}
      />

      {isBasketEmpty() && renderEmptyBasket()}

      <div className="w-full my-10 overflow-hidden md:my-15 lg:mb-20 xl:mb-25">
        <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          <div className="flex flex-wrap -mx-3 -my-5 xl:-mx-5">
            <div className="w-full px-3 my-5 lg:w-8/12 xl:w-9/12 xl:px-5">
              <div className="w-full overflow-x-auto">
                {shippingMessage && (
                  <div className="w-full mb-5">
                    <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
                      <div className="p-4 mt-4 bg-white border rounded-md border-red">
                        <strong>
                          {shippingMessage}
                        </strong>
                        <p className="w-full mt-2 underline">
                          <span className="cursor-pointer" onClick={() => toggleShippingCosts()}>
                            View shipping costs
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid w-full grid-cols-8 mb-12 min-w-182 md:min-w-250 text-xxs md:text-xs xl:text-base">
                  {!isBasketEmpty() && renderCart()}
                </div>
              </div>
            </div>

            <div className="w-full px-3 my-5 lg:w-4/12 xl:w-3/12 xl-px-5">
              {cart && (
                <div className="relative w-full py-6 lg:p-6 bg-lightGrey before:bg-lightGrey after:bg-lightGrey content-before content-after before:-left-max after:-right-max lg:content-after-none lg:content-before-none before:absolute after:absolute before:w-max after:w-max before:top-0 after:top-0 before:h-full after:h-full" tw-content-before="" tw-content-after="">
                  <h2 className="text-md">
                    Summary
                  </h2>

                  <div className="w-full mt-12 md:w-2/3 lg:w-full">
                    <h3 className="block mb-3 font-bold">
                      Order Summary
                    </h3>

                    <div className="flex flex-wrap -my-2">
                      <div className="flex flex-wrap justify-between w-full my-2">
                        <span className="block mr-4">
                          Items:
                        </span>

                        <strong>
                          {displayPrice(cart.cart_amount, cart.currency.code)}
                        </strong>
                      </div>

                      <div className="flex justify-between w-full my-2">
                        <span className="block mr-4">
                          VAT:
                        </span>

                        <strong>
                          {displayPrice(tax, cart.currency.code)}
                        </strong>
                      </div>

                      <div className="flex justify-between w-full my-2">
                        <span className="block mr-4">
                          Total:
                        </span>

                        <strong>
                          {displayPrice(cart.cart_amount + tax, cart.currency.code)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {!isBlocked() && (
                    <div className="w-full mt-6 sm:w-2/3 lg:w-full">
                      <p className="w-full">
                        Total will be billed to
                        {' '}
                        {account.stockist.name}
                        <br />
                        <br />
                        Shipping will be charged at your standard rate as per your T&amp;C&apos;s
                      </p>

                      <p className="w-full mt-2 underline">
                        <span className="cursor-pointer" onClick={() => toggleShippingCosts()}>
                          View shipping costs
                        </span>
                      </p>
                    </div>
                  )}

                  <div
                    className="w-full mt-12"
                    ref={deliveryRef}
                  >
                    <h3 className="flex items-center justify-between mt-6 mb-3 font-bold md:justify-start lg:justify-between">
                      Delivery Address

                      <InfoBubble
                        className="w-6 h-6 ml-2"
                        tooltip="Choose a delivery address to send your orders to."
                      />
                    </h3>

                    <div className="w-full sm:w-2/3 lg:w-full">
                      <DeliverySequence
                        value={sequenceId}
                        placeholder="Please select a delivery sequence"
                        className={classNames('w-full', { 'border-red': !deliveryValid })}
                        onChange={(id) => handleDeliverySequenceChange(id)}
                      />
                    </div>
                  </div>

                  {!isBlocked() && (
                    <div className="w-full mt-12">
                      <h3 className="block mt-6 mb-3 font-bold">
                        Dispatch Date
                      </h3>

                      <div className="w-full">
                        <div className="w-full -my-2">
                          {dispatchOptions.map((item, index) => (
                            <RadioButton
                              key={index}
                              label={item.label}
                              value={item.value}
                              onChange={() => handleDispatchChange(item.value)}
                              checked={selectedDispatch === item.value}
                              className="w-full my-2"
                            />
                          ))}

                          {selectedDispatch === 'custom' && (
                            <DatePicker
                              className="block w-full my-2"
                              value={customDispatchDate}
                              onChange={handleCustomDispatchDateChange}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {!isBlocked() && (
                    <div className="w-full mt-12">
                      <h3 className="block mt-6 mb-3 font-bold">
                        Delivery Address
                      </h3>

                      {shippingAddress.length && (
                        <address className="w-full not-italic">
                          {shippingAddress.map((line, index) => (
                            <span key={`address-line-${index}`}>
                              {convertToTitleCase(line)}
                              <br />
                            </span>
                          ))}
                        </address>
                      )}
                    </div>
                  )}

                  {!isBlocked() && (
                    <div className="w-full mt-6 sm:w-2/3 lg:w-full">
                      <label className="w-full">
                        <span className="block w-full">
                          Customer reference (optional)
                        </span>

                        <input
                          type="text"
                          placeholder="Type here..."
                          maxLength="16"
                          value={customerReference}
                          onChange={handleCustomerReferenceChange.bind(this)}
                          className="block w-full px-4 py-6 mt-4 transition-colors duration-150 ease-in-out bg-white border border-transparent outline-none placeholder-grey h-15 hover:border-charcoal focus:border-charcoal"
                        />
                      </label>
                    </div>
                  )}

                  {!isBlocked() && (
                    <div className="w-full mt-12 sm:w-2/3 lg:w-full">
                      <h3 className="block mb-3 font-bold">
                        Confirm
                      </h3>

                      <span className="w-full">
                        By placing your order you agree to Panache&apos;s
                        {' '}
                        <a href="/terms" target="_blank" className="underline hover:no-underline focus:no-underline">
                          Terms &amp; Conditions
                        </a>
                      </span>
                    </div>
                  )}

                  {!isBlocked() && (
                    <Button
                      theme="green-to-pink"
                      className="w-full mt-6 min-w-37 sm:w-auto"
                      busy={busy}
                      disabled={busy}
                      onClick={submit}
                    >
                      Place order
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {quickOrder && (
        <QuickOrder onClose={() => toggleQuickOrder()} />
      )}

      {shippingCosts && (
        <ShippingCosts onClose={() => toggleShippingCosts()} />
      )}
    </>
  )
}

ShopCart.propTypes = {
  account: PropTypes.object,
  cart: PropTypes.object,
  updateCart: PropTypes.func,
  stockistId: PropTypes.number,
  stockists: PropTypes.array
}

const mapStateToProps = (state) => ({
  account: state.auth.account,
  cart: state.shop.cart,
  stockists: state.auth.account.stockists,
  stockistId: state.auth.account.stockist?.id
})

const mapDispatchToProps = {
  updateCart
}

export default connect(mapStateToProps, mapDispatchToProps)(ShopCart)
